import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export interface PushBroadcastInput {
  title: { it?: string; en?: string };
  body: { it?: string; en?: string };
  data?: Record<string, string>;
}

export interface PushBroadcastResponse {
  sent: boolean;
  recipientCount: number;
}

export function useSendPushBroadcast() {
  return useMutation({
    mutationFn: async (input: PushBroadcastInput) => {
      const res = await apiClient.post<PushBroadcastResponse>('/notifications/push', input);
      return res.data;
    },
  });
}

/** T-156 — invio push a un SINGOLO tifoso (stesso comporre del broadcast + destinatario). */
export interface PushUserInput extends PushBroadcastInput {
  userId: string;
}

export interface PushUserResponse {
  sent: boolean;
  userId: string;
}

export function useSendPushToUser() {
  return useMutation({
    mutationFn: async (input: PushUserInput) => {
      const res = await apiClient.post<PushUserResponse>('/notifications/push-user', input);
      return res.data;
    },
  });
}

// ─── T-209 — notifiche push PROGRAMMATE ────────────────────────────────────

export type ScheduledStatus = 'SCHEDULED' | 'SENDING' | 'SENT' | 'CANCELLED' | 'FAILED';
export type ScheduledRepeatFreq = 'DAILY' | 'WEEKLY';

export interface ScheduledNotification {
  id: string;
  title: { it?: string; en?: string };
  body: { it?: string; en?: string };
  data: Record<string, string> | null;
  categoryFilter: 'matches' | 'shop' | 'receipts' | 'posts' | null;
  status: ScheduledStatus;
  repeatFreq: ScheduledRepeatFreq | null;
  repeatDays: number[] | null;
  localTime: string | null;
  timezone: string;
  nextRunAt: string | null;
  lastRunAt: string | null;
  occurrenceCount: number;
  createdAt: string;
  updatedAt: string;
}

/** Corpo per creare/modificare una notifica programmata. */
export interface ScheduledInput {
  title?: { it?: string; en?: string };
  body?: { it?: string; en?: string };
  data?: Record<string, string>;
  categoryFilter?: 'matches' | 'shop' | 'receipts' | 'posts';
  /** Assente = una-tantum. */
  repeatFreq?: ScheduledRepeatFreq;
  /** Solo una-tantum: "YYYY-MM-DDTHH:mm" (ora locale). */
  scheduledAtLocal?: string;
  /** Solo periodica: "HH:mm". */
  localTime?: string;
  /** Solo WEEKLY: giorni ISO 1..7. */
  repeatDays?: number[];
  timezone?: string;
}

const SCHEDULED_KEY = ['notifications', 'scheduled'] as const;

export function useScheduledNotifications(status?: ScheduledStatus) {
  return useQuery({
    queryKey: [...SCHEDULED_KEY, status ?? 'all'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: ScheduledNotification[] }>(
        '/notifications/scheduled',
        { params: status ? { status } : undefined },
      );
      return res.data.data;
    },
  });
}

export function useCreateScheduled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ScheduledInput) => {
      const res = await apiClient.post<ScheduledNotification>('/notifications/scheduled', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SCHEDULED_KEY }),
  });
}

export function useUpdateScheduled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: ScheduledInput }) => {
      const res = await apiClient.patch<ScheduledNotification>(
        `/notifications/scheduled/${id}`,
        input,
      );
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SCHEDULED_KEY }),
  });
}

export function useSendScheduledNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post<{ sent: boolean; scheduleKept: boolean }>(
        `/notifications/scheduled/${id}/send-now`,
      );
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SCHEDULED_KEY }),
  });
}

export function useCancelScheduled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.delete<ScheduledNotification>(`/notifications/scheduled/${id}`);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SCHEDULED_KEY }),
  });
}
